import React, { useMemo, useState } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { ChevronDown, Check } from 'lucide-react';

/**
 * Generic multi-select dropdown with a "Select All" row.
 * `selected` is the explicit list of chosen values. Callers initialise it to the full option
 * list (= everything selected). An empty list means nothing is selected.
 */
export const MultiSelect = ({
  options = [],
  selected = [],
  onChange,
  placeholder = 'Select…',
  allLabel = 'All',
  emptyLabel = null,
  testId,
  emptyText = 'No options',
}) => {
  const [open, setOpen] = useState(false);
  const selSet = useMemo(() => new Set(selected), [selected]);
  const allSelected = options.length > 0 && selected.length === options.length;

  const label = useMemo(() => {
    if (options.length === 0) return emptyText;
    if (allSelected) return allLabel;
    if (selected.length === 0) return emptyLabel || 'None selected';
    if (selected.length === 1) return selected[0];
    return `${selected.length} selected`;
  }, [options, allSelected, selected, allLabel, emptyLabel, emptyText]);

  const toggleOne = (opt) => {
    const next = new Set(selSet);
    if (next.has(opt)) next.delete(opt); else next.add(opt);
    onChange(options.filter((o) => next.has(o)));
  };

  const toggleAll = () => {
    onChange(allSelected ? [] : [...options]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          className="w-full justify-between font-normal"
          data-testid={testId}
        >
          <span className={selected.length === 0 && !allSelected ? 'text-slate-400' : ''}>
            {label || placeholder}
          </span>
          <ChevronDown className="w-4 h-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[220px]" align="start">
        <div className="max-h-64 overflow-y-auto py-1">
          {options.length === 0 && (
            <p className="text-xs text-slate-500 text-center py-3">{emptyText}</p>
          )}
          {options.length > 0 && (
            <label
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium cursor-pointer hover:bg-slate-50 border-b"
              data-testid={testId ? `${testId}-all` : undefined}
            >
              <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
              <span className="flex items-center gap-1">
                <Check className="w-3.5 h-3.5 text-emerald-600" /> Select All
              </span>
            </label>
          )}
          {options.map((opt) => (
            <label
              key={opt}
              className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50"
              data-testid={testId ? `${testId}-opt-${opt}` : undefined}
            >
              <Checkbox checked={selSet.has(opt)} onCheckedChange={() => toggleOne(opt)} />
              <span className="flex-1">{opt}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
